/**
 * CustomerType term resolution ladders — the pure companions to
 * useRelayLoadTerm / useSiteTerm / useSiteGroupTerm.
 *
 * A customer's relay loads, sites, and site groups are named differently per
 * market: OOH operators call them "Panels"/"Structures"/"Plants", commercial
 * customers "Zones"/"Sites"/"Groups". All three terms resolve through the same
 * three-layer ladder: the customer's CustomerType override, then the
 * tenant-wide TenantConfig term, then an i18n default. Every raw value
 * (CustomerType or tenant) is treated as an i18n key, resolved through the
 * caller's translate fn with the raw value as its own `defaultValue`, so a
 * legacy plain-literal term (e.g. "Zone") still renders verbatim while a
 * configured key (e.g. "relay.termPanelSingular") localizes.
 *
 * Each field falls back independently: a blank (or absent) term uses the next
 * layer down, so a half-configured type/tenant yields the next layer's value
 * for the missing field rather than a mismatched pair.
 *
 * Contains:
 * - resolveScopeCustomerTypeId — (scope) => CustomerType id | null
 * - resolveRelayLoadTerm / resolveSiteTerm / resolveSiteGroupTerm —
 *   (customerType, tenantInfo, defaults, translate) => { singular, plural },
 *   folding in the tenant-level layer between the CustomerType override and
 *   the i18n default. Naming bridge: the site-group term reads
 *   CustomerType.site_group_term but falls back to TenantConfig.sitegroup_term
 *   (one word) — the two models spell the field differently.
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * Resolves the scope customer's CustomerType id from the app-layer ScopeContext.
 * Every scope route family provides that context with a plain-JS customer — the
 * customer/site-group scope Layouts and the site-dashboard layout alike — so
 * this reads one channel. Returns null when no customer or type is in scope.
 *
 * @param {?{ customer?: object }} scope - ScopeContext value (plain-JS customer)
 *   or null when mounted above any scope Layout.
 * @returns {?(string|number)} the CustomerType id (ref id or bare id), or null
 *   when the scope carries no customer/type.
 */
export function resolveScopeCustomerTypeId(scope) {
  const customerType = scope?.customer?.customer_type ?? null;
  return customerType?.id ?? customerType ?? null;
}

/**
 * @param {?object} customerType - resolved CustomerType catalog row, or null
 *   when no customer/type is in scope. Reads relay_load_term /
 *   relay_load_term_plural.
 * @param {?object} tenantInfo - the tenant slice's info object (already seeded
 *   from TENANT_DEFAULTS, so always present). Reads relay_load_term /
 *   relay_load_term_plural.
 * @param {{ singular: string, plural: string }} defaults - i18n literal fallback
 *   used when both the CustomerType and tenant layers are blank/absent.
 * @param {Function} [translate] - i18next-shaped `t(key, { defaultValue })`.
 *   Defaults to an identity fallback (returns `defaultValue`) so the module
 *   stays usable without i18n wired in.
 * @returns {{ singular: string, plural: string }} The customer-facing relay-load
 *   term pair.
 */
export function resolveRelayLoadTerm(customerType, tenantInfo, defaults, translate) {
  const t = typeof translate === 'function' ? translate : (key, opts) => (opts && opts.defaultValue) ?? key;
  const singular =
    resolveTermField(customerType?.relay_load_term, t) ??
    resolveTermField(tenantInfo?.relay_load_term, t) ??
    defaults.singular;
  const plural =
    resolveTermField(customerType?.relay_load_term_plural, t) ??
    resolveTermField(tenantInfo?.relay_load_term_plural, t) ??
    defaults.plural;
  return { singular, plural };
}

/**
 * Resolves one term field through a raw-value-as-i18n-key translation,
 * treating a blank/absent value as "not set" (falls through to the caller's
 * next layer) rather than as an empty string.
 *
 * @param {?string} raw - the layer's raw term value, or null/undefined/blank.
 * @param {Function} translate - i18next-shaped `t(key, { defaultValue })`.
 * @returns {?string} the translated term, or null when the layer has no value.
 */
function resolveTermField(raw, translate) {
  const trimmed = raw?.trim();
  return trimmed ? translate(trimmed, { defaultValue: trimmed }) : null;
}

/**
 * @param {?object} customerType - resolved CustomerType catalog row, or null
 *   when no customer/type is in scope. Reads site_term / site_term_plural.
 * @param {?object} tenantInfo - the tenant slice's info object (already seeded
 *   from TENANT_DEFAULTS, so always present). Reads site_term / site_term_plural.
 * @param {{ singular: string, plural: string }} defaults - i18n literal fallback
 *   used when both the CustomerType and tenant layers are blank/absent.
 * @param {Function} [translate] - i18next-shaped `t(key, { defaultValue })`.
 *   Defaults to an identity fallback so the module stays usable without i18n.
 * @returns {{ singular: string, plural: string }} The customer-facing site term pair.
 */
export function resolveSiteTerm(customerType, tenantInfo, defaults, translate) {
  const t = typeof translate === 'function' ? translate : (key, opts) => (opts && opts.defaultValue) ?? key;
  const singular =
    resolveTermField(customerType?.site_term, t) ?? resolveTermField(tenantInfo?.site_term, t) ?? defaults.singular;
  const plural =
    resolveTermField(customerType?.site_term_plural, t) ??
    resolveTermField(tenantInfo?.site_term_plural, t) ??
    defaults.plural;
  return { singular, plural };
}

/**
 * @param {?object} customerType - resolved CustomerType catalog row, or null
 *   when no customer/type is in scope. Reads site_group_term / site_group_term_plural.
 * @param {?object} tenantInfo - the tenant slice's info object (already seeded
 *   from TENANT_DEFAULTS, so always present). Reads sitegroup_term (one word)
 *   / sitegroup_term_plural.
 * @param {{ singular: string, plural: string }} defaults - i18n literal fallback
 *   used when both the CustomerType and tenant layers are blank/absent.
 * @param {Function} [translate] - i18next-shaped `t(key, { defaultValue })`.
 *   Defaults to an identity fallback so the module stays usable without i18n.
 * @returns {{ singular: string, plural: string }} The customer-facing site-group term pair.
 */
export function resolveSiteGroupTerm(customerType, tenantInfo, defaults, translate) {
  const t = typeof translate === 'function' ? translate : (key, opts) => (opts && opts.defaultValue) ?? key;
  const singular =
    resolveTermField(customerType?.site_group_term, t) ??
    resolveTermField(tenantInfo?.sitegroup_term, t) ??
    defaults.singular;
  const plural =
    resolveTermField(customerType?.site_group_term_plural, t) ??
    resolveTermField(tenantInfo?.sitegroup_term_plural, t) ??
    defaults.plural;
  return { singular, plural };
}
